import React, { useState, useMemo } from 'react';
import {
  calcPricing,
  getUpcomingWednesdays,
  getRunSchedule,
  getPrintAdPrice,
  formatDate,
  formatShortDate,
  toDateInputValue,
  getSpecialEdition,
  getPromoHint,
  fmtDollars,
  getOnlyIssue,
  formatOrderCutoff,
  getAdDue,
} from '../utils/pricing';
import { PRINT_ADS, DIGITAL_ADS } from '../config';
import { getDigitalAvailability } from '../utils/availability';
import DateSelect from './DateSelect';

const WEEKS_OPTIONS = Array.from({ length: 26 }, (_, i) => i + 1);

// 'Fri, Nov 20'
function fmtDay(date) {
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

// Step 4 for a one-issue special: the run date is fixed, so the pickers give
// way to one card holding the date and both deadlines, then plain prices.
function LockedRun({ printAd, printPrice, issue, pricing, hasDigital, conflicts, onRemoveDigital, error, onNext, onBack }) {
  const edition  = getSpecialEdition(issue);
  const [weekday, ...rest] = formatDate(issue).split(', '); // 'Wednesday', 'November 25', '2026'

  return (
    <div className="step-card">
      <h2 className="step-heading">Your run date</h2>
      <p className="step-subheading">
        Nothing to choose here. The {printAd.label} runs in one issue only.
      </p>

      <div className="run-card">
        <div className="run-card-top">
          <span className="run-card-label">Runs once</span>
          {edition && <span className="se-label">{edition.name}</span>}
        </div>
        <div className="run-card-date">
          {weekday}, <span className="nowrap">{rest.join(', ')}</span>
        </div>
        <ul className="run-card-deadlines">
          <li><strong>Order by</strong> <span className="nowrap">{formatOrderCutoff(issue)}</span></li>
          <li>
            <strong>Ad files due</strong> <span className="nowrap">{fmtDay(getAdDue(issue))},</span>{' '}
            <span className="nowrap">by end of day</span>
          </li>
        </ul>
      </div>

      <div className="price-breakdown price-breakdown-locked">
        <div className="price-row">
          <span>
            {printAd.label} (print) <span className="nowrap">&middot; {formatShortDate(issue).replace(/, \d{4}$/, '')} issue</span>
          </span>
          <span>{fmtDollars(printPrice)}</span>
        </div>
        {hasDigital && pricing.digitalLines.map(line => {
          const ad = DIGITAL_ADS.find(a => a.id === line.id);
          if (!ad) return null;
          return (
            <div key={line.id} className="price-row">
              <span>{ad.label} (digital) <span className="nowrap">&middot; same week</span></span>
              <span>{fmtDollars(line.unit * line.weeks)}</span>
            </div>
          );
        })}
        <div className="price-row total">
          <span>Total</span>
          <span>{fmtDollars(pricing.total)}</span>
        </div>
      </div>

      {conflicts.map(({ ad }) => (
        <div key={ad.id} className="availability-warning">
          <div className="availability-warning-title">
            {ad.label} is sold out for {formatShortDate(issue)}
          </div>
          <div className="availability-warning-hint">
            Remove it to continue. Your {printAd.label.toLowerCase()} isn&rsquo;t affected.
          </div>
          <button
            type="button"
            className="availability-remove-btn"
            style={{ marginTop: 8 }}
            onClick={() => onRemoveDigital(ad.id)}
          >
            Remove it
          </button>
        </div>
      ))}

      {error && <p className="step-error">{error}</p>}

      <div className="nav-buttons">
        <button className="btn btn-secondary" onClick={onBack}>&larr; Back</button>
        <button className="btn btn-primary"    onClick={onNext}>Continue &rarr;</button>
      </div>
    </div>
  );
}

export default function Step3Duration({ form, setForm, availability, onNext, onBack }) {
  const [error, setError] = useState('');
  const wednesdays = useMemo(() => getUpcomingWednesdays(52), []);

  const pricing     = calcPricing(form);
  const runSchedule = getRunSchedule(form.startDate, form.weeks);

  // Web/email slots already full on one or more of the selected weeks (skipped
  // weeks don't count — the ad isn't running then).
  const runScheduleKeys = runSchedule.map(toDateInputValue);
  const digiAvail = getDigitalAvailability(availability, runSchedule, form.digitalSkips || {});
  const conflicts = (form.digitalAds || [])
    .map(id => ({ ad: DIGITAL_ADS.find(a => a.id === id), info: digiAvail[id] }))
    .filter(c => c.ad && c.info && c.info.soldOut);

  const hasPrint    = form.medium === 'print'   || form.medium === 'both';
  const hasDigital  = form.medium === 'digital'  || form.medium === 'both';
  const isNonprofit = form.orgType === 'nonprofit';
  const printAd     = hasPrint && form.printAd ? PRINT_ADS.find(a => a.id === form.printAd) : null;
  const printPrice  = printAd ? getPrintAdPrice(printAd, isNonprofit) : 0;

  const selectedDateStr = form.startDate ? toDateInputValue(form.startDate) : '';

  const promoHint    = getPromoHint(pricing);
  const totalSavings = pricing.totalSavings + pricing.promoSavings;
  const savingsSource = pricing.promoApplies
    ? (pricing.totalSavings > 0
        ? `with the ${pricing.promo.label} and your multi-week booking`
        : `with the ${pricing.promo.label}`)
    : `with a ${form.weeks}-week booking`;

  function setWeeks(val) {
    setForm(f => ({ ...f, weeks: Number(val), weeksTouched: true }));
  }

  function setStartDate(val) {
    if (!val) { setForm(f => ({ ...f, startDate: null })); return; }
    const matched = wednesdays.find(d => toDateInputValue(d) === val);
    setForm(f => ({ ...f, startDate: matched || null }));
    setError('');
  }

  function removeDigital(id) {
    setForm(f => {
      const nextSkips = { ...(f.digitalSkips || {}) };
      delete nextSkips[id];
      return { ...f, digitalAds: (f.digitalAds || []).filter(x => x !== id), digitalSkips: nextSkips };
    });
    setError('');
  }

  // Skip a placement for specific weeks (it keeps running the other weeks).
  function skipWeeks(id, dates) {
    setForm(f => {
      const current = (f.digitalSkips && f.digitalSkips[id]) || [];
      const merged  = Array.from(new Set([...current, ...dates]));
      return { ...f, digitalSkips: { ...(f.digitalSkips || {}), [id]: merged } };
    });
    setError('');
  }

  // Undo all skipped weeks for a placement (it runs every week again).
  function unskipAll(id) {
    setForm(f => {
      const nextSkips = { ...(f.digitalSkips || {}) };
      delete nextSkips[id];
      return { ...f, digitalSkips: nextSkips };
    });
  }

  function handleNext() {
    if (!form.startDate) { setError('Please select a start date.'); return; }
    if (conflicts.length) {
      setError('One or more digital placements are sold out for your selected dates. Remove them or pick different dates to continue.');
      return;
    }
    onNext();
  }

  // Format a YYYY-MM-DD slot date for display in the conflict notice.
  function fmtSlot(ymd) {
    const [y, m, d] = ymd.split('-').map(Number);
    return formatShortDate(new Date(y, m - 1, d));
  }

  // A one-issue special (e.g. the Season of Giving spread) has nothing to pick:
  // show its single run date and what it costs.
  const onlyIssue = printAd ? getOnlyIssue(printAd.id) : null;
  if (onlyIssue) {
    return (
      <LockedRun
        printAd={printAd}
        printPrice={printPrice}
        issue={onlyIssue}
        pricing={pricing}
        hasDigital={hasDigital}
        conflicts={conflicts}
        onRemoveDigital={removeDigital}
        error={error}
        onNext={handleNext}
        onBack={onBack}
      />
    );
  }

  return (
    <div className="step-card">
      <h2 className="step-heading">Choose duration &amp; start date</h2>
      <p className="step-subheading">
        Ads run every Wednesday. Select how many weeks and when you'd like to begin.
      </p>

      <div className="duration-row">
        <div>
          <label className="field-label" htmlFor="weeks-select">Number of weeks</label>
          <select
            id="weeks-select"
            className={form.weeksTouched ? undefined : 'weeks-pulse'}
            value={form.weeks}
            onChange={e => setWeeks(e.target.value)}
          >
            {WEEKS_OPTIONS.map(n => (
              <option key={n} value={n}>
                {n} week{n !== 1 ? 's' : ''}
                {n >= 8 ? '  — $20 off per week' : n >= 4 ? '  — $10 off per week' : ''}
              </option>
            ))}
          </select>
          {/* Names the reason the total looks big before the advertiser has to
              work it out from the breakdown below. Retires with the pulse. */}
          {!form.weeksTouched && (
            <div className="weeks-hint">
              Your total covers all {form.weeks} weeks &mdash; change it here.
            </div>
          )}
        </div>

        <div>
          <label className="field-label">Start date (Wednesdays)</label>
          <DateSelect
            wednesdays={wednesdays}
            value={selectedDateStr}
            onChange={setStartDate}
            promo={pricing.promo}
          />
        </div>
      </div>

      {/* Savings callout */}
      {totalSavings > 0 && (
        <div className="savings-callout">
          You save {fmtDollars(totalSavings)} {savingsSource}!
        </div>
      )}

      {/* Price breakdown */}
      <div className="price-breakdown">
        {/* Each line item: rate × weeks = subtotal */}
        {printAd && (
          <div className="price-row">
            <span>{printAd.label} (print)</span>
            <span>
              ${printPrice} &times; {form.weeks} week{form.weeks !== 1 ? 's' : ''}{' '}
              = {fmtDollars(printPrice * form.weeks)}
            </span>
          </div>
        )}

        {hasDigital && pricing.digitalLines.map(line => {
          const ad = DIGITAL_ADS.find(a => a.id === line.id);
          if (!ad) return null;
          const skipDates = (form.digitalSkips?.[line.id] || []).filter(d => runScheduleKeys.includes(d));
          return (
            <div key={line.id} className="price-row" style={{ display: 'block' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>{ad.label} (digital)</span>
                <span>
                  ${line.unit} &times; {line.weeks} week{line.weeks !== 1 ? 's' : ''}{' '}
                  = {fmtDollars(line.unit * line.weeks)}
                </span>
              </div>
              {skipDates.length > 0 && (
                <div className="price-skip-note">
                  skips {skipDates.map(fmtSlot).join(', ')} &mdash;{' '}
                  <button type="button" className="availability-remove-btn" onClick={() => unskipAll(line.id)}>
                    undo
                  </button>
                </div>
              )}
            </div>
          );
        })}

        {/* Discount row — shown as a total dollar amount off, not per-week */}
        {pricing.discountPerWeek > 0 && (
          <div className="price-row discount-row">
            <span>
              {form.weeks >= 8 ? '8+-week' : '4–7 week'} discount
              &nbsp;(${pricing.discountPerWeek} off &times; {form.weeks} weeks)
            </span>
            <span>&minus;{fmtDollars(pricing.totalSavings)}</span>
          </div>
        )}

        {/* Flash-sale row — stacks on top of the multi-week discount above */}
        {pricing.promoApplies && (
          <div className="price-row discount-row">
            <span>
              {pricing.promo.label}
              &nbsp;(${pricing.promoPerWeek} off &times; {form.weeks} week{form.weeks !== 1 ? 's' : ''})
            </span>
            <span>&minus;{fmtDollars(pricing.promoSavings)}</span>
          </div>
        )}

        <div className="price-row total">
          <span>Estimated Total</span>
          <span>{fmtDollars(pricing.total)}</span>
        </div>
      </div>

      {/* Why an active sale link isn't discounting this order yet */}
      {promoHint && <div className="promo-hint">{promoHint}</div>}

      {/* Run schedule */}
      {runSchedule.length > 0 && (
        <div className="run-schedule">
          <div className="run-schedule-title">
            Your ad run schedule &mdash; {runSchedule.length} issue{runSchedule.length !== 1 ? 's' : ''}
          </div>
          <ul>
            {runSchedule.map((d, i) => {
              const se = getSpecialEdition(d);
              return (
                <li key={i}>
                  {formatDate(d)}
                  {se && <span className="se-label">{se.name}</span>}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {conflicts.length > 0 && (
        <div className="availability-warning">
          <div className="availability-warning-title">
            Some digital placements aren’t available for these dates
          </div>
          <ul>
            {conflicts.map(({ ad, info }) => {
              const multi = info.fullDates.length > 1;
              return (
                <li key={ad.id}>
                  <strong>{ad.label}</strong> is sold out for{' '}
                  {info.fullDates.map(fmtSlot).join(', ')}.{' '}
                  <button
                    type="button"
                    className="availability-skip-btn"
                    onClick={() => skipWeeks(ad.id, info.fullDates)}
                  >
                    Skip {multi ? 'those weeks' : 'that week'}
                  </button>
                  <span className="availability-or"> or </span>
                  <button
                    type="button"
                    className="availability-remove-btn"
                    onClick={() => removeDigital(ad.id)}
                  >
                    remove entirely
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="availability-warning-hint">
            Skipping keeps the placement running your other weeks; removing drops it from the whole campaign.
            You can also pick a different start date or number of weeks above.
          </div>
        </div>
      )}

      {error && <p className="step-error">{error}</p>}

      <div className="nav-buttons">
        <button className="btn btn-secondary" onClick={onBack}>&larr; Back</button>
        <button className="btn btn-primary"    onClick={handleNext}>Continue &rarr;</button>
      </div>
    </div>
  );
}
